import { useCallback, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { postReaction } from '@/services/reactions'
import { useGetstreamClient } from '@/lib/hooks/useGetstreamClient'
import {
  CachedReaction,
  OwnReaction,
} from '@/utils/reactions/cacheActivitiesReactions'

import { QueryKeys } from '@/enums'

import { ResponseType } from '../useDailyLimitToast'
import { useErrorBoundary } from '../useErrorBoundary'

export const useReaction = (reactionActivityId: UUID) => {
  const { client } = useGetstreamClient()
  const queryClient = useQueryClient()
  const { handleError } = useErrorBoundary()
  const reactionQueryKey = useMemo(
    () => [QueryKeys.Reaction, reactionActivityId],
    [reactionActivityId]
  )

  const setIsReactionLoading = (isLoading: boolean) => {
    queryClient.setQueryData<CachedReaction>(
      reactionQueryKey,
      (cachedReaction) => ({
        ...cachedReaction,
        isLoading,
      })
    )
  }

  const { mutate: mutatePostReaction } = useMutation({
    mutationFn: postReaction,
    onMutate: () => {
      setIsReactionLoading(true)
      addReactionInCache({})
    },
    onSuccess: (reaction) => {
      addReactionInCache(reaction)
    },
    onError: (error: { response: ResponseType }) =>
      handleError(error, () => removeReactionInCache(undefined)),
    onSettled: () => {
      setIsReactionLoading(false)
    },
  })

  const { mutate: mutateDeleteReaction } = useMutation({
    mutationFn: ({ reactionId }: { reactionId: UUID }) =>
      client.reactions.delete(reactionId),
    onMutate: ({ reactionId }) => {
      setIsReactionLoading(true)

      const cachedActivityReactions =
        queryClient.getQueryData<CachedReaction>(reactionQueryKey)

      const reaction = cachedActivityReactions.ownReactions.find(
        (reaction) => reaction.id === reactionId
      )
      removeReactionInCache(reactionId)

      return reaction
    },
    onError: (error: { response: ResponseType }, _variables, context) =>
      handleError(error, () => addReactionInCache(context)),
    onSettled: () => {
      setIsReactionLoading(false)
    },
  })

  const { data: reaction } = useQuery<CachedReaction>({
    queryKey: reactionQueryKey,
  })

  const addReactionInCache = useCallback(
    (reaction: OwnReaction) => {
      queryClient.setQueryData<CachedReaction>(
        reactionQueryKey,
        (cachedReaction) => {
          const updatedOwnReactions = [...cachedReaction.ownReactions]
          const lastOwnReaction =
            updatedOwnReactions[updatedOwnReactions.length - 1]

          if (lastOwnReaction && !lastOwnReaction.id) updatedOwnReactions.pop()

          return {
            reactionsCount:
              lastOwnReaction && !lastOwnReaction.id
                ? cachedReaction.reactionsCount
                : cachedReaction.reactionsCount + 1,
            ownReactions: [...updatedOwnReactions, reaction],
            isLoading: cachedReaction.isLoading,
          }
        }
      )
    },
    [reactionQueryKey, queryClient]
  )

  const removeReactionInCache = useCallback(
    (reactionId: UUID | undefined) => {
      queryClient.setQueryData<CachedReaction>(
        reactionQueryKey,
        (cachedReaction) => {
          return {
            reactionsCount: Math.max(cachedReaction.reactionsCount - 1, 0),
            ownReactions: cachedReaction.ownReactions.filter(
              (reaction) => reaction.id !== reactionId
            ),
            isLoading: cachedReaction.isLoading,
          }
        }
      )
    },
    [reactionQueryKey, queryClient]
  )

  return {
    addReaction: mutatePostReaction,
    deleteReaction: mutateDeleteReaction,
    reaction,
    addReactionInCache,
    removeReactionInCache,
  }
}
